import { useState } from "react";
import {
  Check,
  Star,
  Zap,
  Crown,
  ChevronDown,
  ChevronUp,
  Phone,
  X,
} from "lucide-react";
import { subscriptionService } from "../../../../services/api/settings";
import { toast } from "sonner";
import ContactSalesModal from "./ContactSalesModal";
// import PaymentSuccessModal from "./PaymentSuccessModal";
import PaymentFailedModal from "./PaymentFailedModal";
import {
  Feature,
  OfferingPlan,
  OfferingAddon,
} from "../../../../store/types/settings";
import formatPriceString from "../../../../utility/formatPriceString";
import formatPrice from "../../../../utility/formatPrice";
// import { SubscribeNow } from "../../../store/types/settings";

// Types
export type Tier = "STARTER" | "GROWTH" | "SCALE" | "ENTERPRISE";
export type Provider = "paypal" | "paystack";
export type Frequency = "MONTHLY" | "QUARTERLY" | "YEARLY";

export interface SubscribeNow {
  tier: Tier;
  provider: Provider;
  frequency: Frequency;
  organizationId: string;
  directPlan?: {
    tier: "STARTER" | "GROWTH" | "SCALE";
    includeSetupFee: boolean;
  };
  lifecyclePhaseIds?: number[];
}

export interface PricingPlansProps {
  billingType: "Monthly" | "Quarterly" | "Yearly";
  organizationId: string;
  selectedTier?: string;
  selectedFeatureIds?: string[];
  selectedFeatures?: Feature[];
  onFeatureRemove?: (featureId: string) => void;
  provider?: string;
  onBankTransferClick?: () => void;
  directPlan?: {
    tier: "STARTER" | "GROWTH" | "SCALE";
    includeSetupFee: boolean;
  };
  selectedDirectAddOnIds?: string[];
  onDirectAddOnRemove?: (id: string) => void;
  plans?: OfferingPlan[];
  addons?: OfferingAddon[];
  selectedLifecyclePhaseIds?: number[];
}

interface Plan {
  name: string;
  badge: string;
  icon: React.ComponentType<any>;
  priceMonthly: string;
  priceQuarterly?: string;
  priceYearly: string;
  monthlyPrice?: string;
  quarterlyPrice?: string | number;
  yearlyPrice?: string;
  integration: string;
  integrationCost: number;
  total?: string;
  totalYearly?: string;
  features: string[];
  integrationServices: Array<{ name: string; cost: string }>;
  popular: boolean;
  gradient: string;
  tier?: Tier;
  isVip?: boolean;
  costBreakdown?: {
    totalCost: number;
    lastUpdated: string;
    deploymentCosts: {
      id: string;
      name: string;
      description: string;
      price: number;
    }[];
    integrationCosts: any[];
    totalDeploymentCost: number;
    totalIntegrationCost: number;
  };
}

export default function PricingPlanPay({
  billingType,
  organizationId: id,
  selectedTier,
  selectedFeatureIds = [],
  selectedFeatures = [],
  onFeatureRemove,
  provider,
  onBankTransferClick,
  directPlan,
  selectedDirectAddOnIds = [],
  onDirectAddOnRemove,
  plans: propPlans,
  addons: propAddons = [],
  selectedLifecyclePhaseIds = [],
}: PricingPlansProps) {
  const [expandedPlan, setExpandedPlan] = useState<number | null>(null);
  const [loading, setLoading] = useState<{ [key: string]: boolean }>({});
  const [showAllPlans, setShowAllPlans] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    tier: "",
    amount: "",
    frequency: "MONTHLY" as Frequency,
  });
  const [failedModal, setFailedModal] = useState({
    isOpen: false,
    errorMessage: "",
  });
  const [lastAttemptedPlan, setLastAttemptedPlan] = useState<Plan | null>(null);

  const getTierIcon = (tier: string | null) => {
    switch (tier?.toUpperCase()) {
      case "STARTER":
        return Zap;
      case "GROWTH":
        return Star;
      case "SCALE":
        return Crown;
      case "ENTERPRISE":
        return Crown;
      default:
        return Zap;
    }
  };

  const directPlans = [
    {
      name: "Starter",
      subtitle: "Small Teams",
      description:
        "Best for Small Agencies & Startups building their first database.",
      monthlyPrice: 689000,
      quarterlyPrice: 2067000,
      yearlyPrice: 6890000,
      setupFee: "4,350,000",
      tier: "STARTER",
    },
    {
      name: "Growth",
      subtitle: "Mid-Sized Companies",
      description:
        "Best for Mid-Sized Companies requiring automation and scanned document processing.",
      monthlyPrice: 1034000,
      quarterlyPrice: 3102000,
      yearlyPrice: 10340000,
      setupFee: "5,200,000",
      tier: "GROWTH",
    },
    {
      name: "Scale",
      subtitle: "Large Corps / Staffing Firms",
      description:
        "Best for Large Corporations & Staffing Firms needing unlimited scale and deep analytics.",
      monthlyPrice: 1609000,
      quarterlyPrice: 4827000,
      yearlyPrice: 16090000,
      setupFee: "22,400,000",
      tier: "SCALE",
    },
    {
      name: "Enterprise",
      subtitle: "Governments & Multinationals",
      description:
        "The absolute top tier for Conglomerates & Governments requiring bespoke AI and multi-tenancy.",
      monthlyPrice: 0,
      quarterlyPrice: 0,
      yearlyPrice: 0,
      setupFee: "Custom",
      tier: "ENTERPRISE",
    },
  ];

  const plans: Plan[] = [
    {
      name: "Starter",
      badge: "For Growing HR Teams",
      icon: Zap,
      priceMonthly: "650,000",
      priceYearly: "7,150,000",
      integration: "1,400,000",
      integrationCost: 1400000,
      total: "2,050,000",
      totalYearly: "9,200,000",
      features: [
        "Up to 50 AI-powered interviews/month",
        "Up to 3 Admin usage",
        "AI-driven job creation system",
        "Basic reporting & grading",
        "Pre-test assessments",
        "15GB video storage (3 months)",
        "Priority support",
      ],
      integrationServices: [
        { name: "Standard Onboarding & Training", cost: "1,400,000" },
        // { name: "Total Integration", cost: "1,400,000" },
      ],
      popular: false,
      gradient: "from-[#2196F3] to-[#1E88E5]",
      tier: "STARTER",
    },
    {
      name: "Growth",
      badge: "Most Popular",
      icon: Star,
      priceMonthly: "950,000",
      priceYearly: "10,450,000",
      integration: "2,800,000",
      integrationCost: 2800000,
      total: "3,750,000",
      totalYearly: "13,250,000",
      features: [
        "Up to 100 AI-powered interviews/month",
        "Up to 5 Admin usage",
        "Comprehensive reporting & analytics",
        "Advanced interview dashboard",
        "30GB video storage (6 months)",
        "Dedicated account manager",
        "Custom integrations & API access",
      ],
      integrationServices: [
        { name: "Standard Onboarding & Training", cost: "800,000" },
        { name: "Data Migration & Import", cost: "1,200,000" },
        { name: "Quality Assurance & Launch Support", cost: "800,000" },
        { name: "Total Integration", cost: "2,800,000" },
      ],
      popular: true,
      gradient: "from-[#1976D2] to-[#0B60D6]",
      tier: "GROWTH",
    },
    {
      name: "Scale",
      badge: "Advanced Integrations",
      icon: Crown,
      priceMonthly: "1,600,000",
      priceYearly: "17,600,000",
      integration: "5,900,000",
      integrationCost: 5900000,
      total: "7,300,000",
      totalYearly: "23,500,000",
      features: [
        "Up to 200 AI-powered interviews/month",
        "Up to 10 Admin usage",
        "Full white-label customization",
        "Custom branding and domain setup",
        "99.5% uptime SLA",
        "100GB video storage (12 months)",
        "Quarterly business reviews",
      ],
      integrationServices: [
        { name: "Standard Onboarding & Training", cost: "800,000" },
        { name: "Data Migration & Import", cost: "1,200,000" },
        { name: "Quality Assurance & Launch Support", cost: "800,000" },
        { name: "Platform Customization & Branding", cost: "1,500,000" },
        { name: "AI Configuration & Model Tuning", cost: "800,000" },
        { name: "System Integration & API Setup", cost: "800,000" },
        { name: "Total Integration", cost: "5,900,000" },
      ],
      popular: false,
      gradient: "from-[#1976D2] to-[#0B60D6]",
      tier: "SCALE",
    },
    {
      name: "Enterprise",
      badge: "Custom Enterprise Solution",
      icon: Crown,
      priceMonthly: "Contact Us",
      priceYearly: "Contact Us",
      integration: "Custom",
      integrationCost: 0,
      features: [
        "Everything in Enterprise Plus",
        "Bespoke integrations",
        "Advanced SLA (99.9% uptime)",
        "Dedicated enterprise support",
      ],
      integrationServices: [
        { name: "Custom Integration Services", cost: "Custom Pricing" },
      ],
      popular: false,
      gradient: "from-[#1E88E5] to-[#1976D2]",
      isVip: true,
      tier: "ENTERPRISE",
      total: "",
      totalYearly: "",
    },
  ];

  // Filter plans based on selectedTier if provided and showAllPlans is false
  const activePlans: Plan[] = (propPlans || [])?.map((plan: OfferingPlan) => ({
    ...plan,
    name: plan.title,
    badge: plan.subtitle,
    icon: getTierIcon(plan.tier) as any,
    priceMonthly: parseFloat(plan.monthlyPrice || "0").toLocaleString(),
    priceQuarterly: plan.quarterlyPrice
      ? parseFloat(String(plan.quarterlyPrice)).toLocaleString()
      : (parseFloat(plan.monthlyPrice || "0") * 3).toLocaleString(),
    priceYearly: parseFloat(plan.yearlyPrice || "0").toLocaleString(),
    integration: parseFloat(
      plan.setupFee || plan.integrationCost || "0",
    ).toLocaleString(),
    integrationCost: parseFloat(plan.setupFee || plan.integrationCost || "0"),
    features: plan.points || [],
    integrationServices:
      (plan.costBreakdown?.deploymentCosts?.length ?? 0) > 0
        ? plan
            .costBreakdown!.deploymentCosts.filter((d: any) => d && d.name)
            .map((d: any) => ({
              name: d.name,
              cost: d.price.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }),
            }))
        : plan.setupFee || plan.integrationCost
          ? [
              {
                name: "Standard Onboarding & Training",
                cost: (
                  parseFloat(plan.setupFee || plan.integrationCost || "0") || 0
                ).toLocaleString(),
              },
            ]
          : [],
    popular: plan.tier === "GROWTH",
    isVip: plan.tier === "ENTERPRISE",
    gradient:
      plan.tier === "ENTERPRISE"
        ? "from-[#1976D2] to-[#0B60D6]"
        : "from-[#2196F3] to-[#1E88E5]",
    tier: plan.tier as any,
    total: "",
    totalYearly: "",
  }));

  const filteredPlans =
    selectedTier && !showAllPlans
      ? activePlans.filter(
          (plan) => plan.tier?.toUpperCase() === selectedTier.toUpperCase(),
        )
      : activePlans;

  // Determine if we should show the "View All Plans" button
  const shouldShowViewAllButton =
    selectedTier && !showAllPlans && filteredPlans.length === 1;

  // Determine if we should show the "Back to Selected Plan" button
  const shouldShowBackButton = selectedTier && showAllPlans;

  const handleSubscription = async (
    plan: Plan,
    provider: Provider = "paypal",
  ) => {
    // Validate organization ID
    if (!id) {
      toast.error("Organization ID is missing");
      return;
    }

    // Handle VIP/Contact Us plans
    if (plan.isVip || !plan.tier) {
      // Handle VIP/Contact Us plans
      if (plan.isVip || !plan.tier) {
        setShowContactModal(true);
        return;
      }
    }

    const loadingKey = `${plan.tier}-${provider}`;

    // Prevent duplicate requests
    if (loading[loadingKey]) return;

    setLoading((prev) => ({ ...prev, [loadingKey]: true }));
    setLastAttemptedPlan(plan);

    try {
      const frequency: Frequency =
        billingType === "Yearly"
          ? "YEARLY"
          : billingType === "Quarterly"
            ? "QUARTERLY"
            : "MONTHLY";

      const subscriptionData: SubscribeNow = {
        tier: plan.tier,
        provider,
        frequency,
        organizationId: id,
        directPlan,
        lifecyclePhaseIds: selectedLifecyclePhaseIds.length > 0 ? selectedLifecyclePhaseIds : undefined,
      };

      console.log("Initiating subscription:", subscriptionData);

      const result = await subscriptionService.subscribe(
        subscriptionData.tier,
        subscriptionData.provider,
        subscriptionData.frequency,
        subscriptionData.organizationId,
        selectedFeatureIds.length > 0 ? selectedFeatureIds : undefined,
        subscriptionData.directPlan,
        undefined, // directFeatureIds
        undefined, // amount
        subscriptionData.lifecyclePhaseIds
      );

      if (result.status === "success" && result.data?.authorizationUrl) {
        // Open payment gateway in new tab
        window.open(result.data.authorizationUrl, "_blank");
        toast.success(`Redirecting to ${provider} payment...`);

        // Show success modal (simulate success for demo - in production, handle via callback)
        // You may want to show this after payment confirmation from gateway
        setTimeout(() => {
          setSuccessModal({
            isOpen: true,
            tier: plan.name,
            amount:
              billingType === "Yearly"
                ? plan.priceYearly
                : billingType === "Quarterly"
                  ? plan.priceQuarterly || (parseFloat(plan.priceMonthly.replace(/[₦,]/g, "")) * 3).toLocaleString()
                  : plan.priceMonthly,
            frequency,
          });
        }, 2000);
      } else {
        // Show failed modal
        setFailedModal({
          isOpen: true,
          errorMessage: "Unable to initiate payment process. Please try again.",
        });
        toast.error("Unable to initiate payment process");
      }
    } catch (error) {
      console.error("Subscription error:", error);
      setFailedModal({
        isOpen: true,
        errorMessage:
          "Something went wrong while processing your request. Please try again.",
      });
      toast.error("Something went wrong while processing your request");
    } finally {
      setLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleRetryPayment = () => {
    if (lastAttemptedPlan) {
      handleSubscription(lastAttemptedPlan);
    }
  };

  const getButtonText = (plan: Plan) => {
    if (plan.isVip) return "Contact Sales";

    const loadingKey = `${plan.tier}-paypal`;
    if (loading[loadingKey]) return "Processing...";

    return "Continue to Order Form";
  };

  // Calculate total price including selected features
  // Calculate total price including selected features
  const calculateTotalPrice = (plan: Plan): string => {
    const basePriceStr =
      billingType === "Monthly"
        ? plan.priceMonthly
        : billingType === "Quarterly"
          ? plan.priceQuarterly || (parseFloat(plan.priceMonthly.replace(/[₦,]/g, "")) * 3).toLocaleString()
          : plan.priceYearly;
    // integrationCost should be the numeric value from the plan object
    const integrationCost =
      plan.integrationCost ||
      parseFloat((plan.integration || "0").replace(/[₦,\s]/g, "")) ||
      0;

    const basePrice = parseFloat(basePriceStr.replace(/[₦,]/g, "")) || 0;

    // Convert feature prices from kobo to naira (divide by 1000)
    const featuresTotal = (selectedFeatures || []).reduce(
      (sum, f) => sum + (f.price || 0),
      0,
    );

    // Direct Plan Cost
    let directPlanCost = 0;
    let setupFee = 0;
    if (directPlan && directPlan.tier) {
      const dPlan = directPlans.find((p) => p.tier === directPlan.tier);
      if (dPlan) {
        directPlanCost =
          billingType === "Monthly"
            ? dPlan.monthlyPrice
            : billingType === "Quarterly"
              ? dPlan.quarterlyPrice || dPlan.monthlyPrice * 3
              : dPlan.yearlyPrice;
        if (
          directPlan.includeSetupFee &&
          dPlan.setupFee &&
          dPlan.setupFee !== "Custom"
        ) {
          setupFee = parseFloat(dPlan.setupFee.replace(/[₦,]/g, "")) || 0;
        }
      }
    }

    // AI/Direct Addons from offerings
    const aiAddOnsTotal = (selectedDirectAddOnIds || []).reduce(
      (sum: number, id: string) => {
        const addon = (propAddons || []).find((a) => a.id === id);
        if (!addon) return sum;
        return (
          sum +
          (typeof addon.price === "string"
            ? parseFloat(addon.price)
            : addon.price || 0) /
            1000
        );
      },
      0,
    );

    const total =
      basePrice +
      integrationCost +
      featuresTotal +
      directPlanCost +
      setupFee +
      aiAddOnsTotal;
    return `${total.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  return (
    <section className="font-abel md:pt-0 md:pb-3 pb-3 bg-gradient-to-b">
      <ContactSalesModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
      />
      <div className="max-w-7xl mx-auto px-3 lg:px-3">
        <div
          className={`grid grid-cols-1 ${
            filteredPlans.length === 1
              ? "md:grid-cols-1 lg:grid-cols-1 max-w-2xl mx-auto"
              : "md:grid-cols-2 lg:grid-cols-4"
          } gap-5`}
        >
          {filteredPlans.map((plan, index) => {
            const Icon = plan.icon;
            const isCustom = plan.priceMonthly === "Contact Us";
            const isExpanded = expandedPlan === index;

            return (
              <div
                key={index}
                className={`relative rounded-[20px] transition-all duration-300 hover:shadow-2xl border ${
                  plan.isVip
                    ? "bg-gradient-to-br from-[#0B60D6] via-[#0B60D6] to-[#0B60D6] border-[#1E88E5] shadow-lg"
                    : plan.popular
                      ? "bg-white border-[#2196F3] shadow-xl md:scale-105"
                      : "bg-white border-[#EEEEEE] shadow-lg"
                } ${isExpanded ? "ring-2 ring-[#42A5F5]" : ""}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="px-4 py-1 bg-gradient-to-r from-[#1976D2] to-[#0B60D6] text-white text-sm font-medium rounded-full shadow-lg">
                      Most Popular
                    </div>
                  </div>
                )}

                <div className="px-5 pt-5 pb-4">
                  {/* <div
                    className={`inline-flex p-3 rounded-[10px] bg-gradient-to-r ${plan.gradient} mb-4`}
                  >
                    <Icon
                      className={`w-6 h-6 ${
                        plan.isVip ? "text-white" : "text-white"
                      }`}
                    />
                  </div> */}

                  <div className="mb-4">
                    <h3
                      className={`text-2xl font-medium mb-2 ${
                        plan.isVip ? "text-white" : "text-[#212121]"
                      }`}
                    >
                      {plan.name}
                    </h3>
                    <span
                      className={`inline-block px-3 py-1 text-sm font-medium rounded-full  border border-[#1976D2] ${
                        plan.isVip
                          ? "text-[#ffffff] bg-[#1976D2]/40"
                          : "text-[#1976D2] bg-[#ffffff]/30"
                      }`}
                    >
                      {plan.badge}
                    </span>
                  </div>

                  <div className="mb-6">
                    {isCustom ? (
                      <div
                        className={`text-3xl font-medium ${
                          plan.isVip ? "text-white" : "text-[#212121]"
                        }`}
                      >
                        {/* Contact Us */}
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between">
                          <div className="flex items-center">
                            <div
                              className={`text-[16px] font-normal mb-1 ${
                                plan.isVip ? "text-white" : "text-[#212121]"
                              }`}
                            >
                              <span className="text-[14px]">N</span>{" "}
                              {billingType === "Monthly"
                                ? formatPriceString(plan?.monthlyPrice || "0")
                                : billingType === "Quarterly"
                                  ? formatPriceString(String(plan?.quarterlyPrice ?? (parseFloat(plan?.monthlyPrice || "0") * 3)))
                                  : formatPriceString(plan?.yearlyPrice || "0")}
                            </div>
                            <div
                              className={`text-[12px] ${
                                plan.isVip ? "text-[#90CAF9]" : "text-gray-600"
                              }`}
                            >
                              /{billingType === "Monthly" ? "month" : billingType === "Quarterly" ? "quarter" : "year"}
                              {/* {billingType.toLowerCase()} */}
                            </div>
                          </div>
                          {/* Total Payment  */}
                          {/* <div>
                            <div
                              className={`text-[18px] font-normal ${
                                plan.isVip ? "text-white" : "text-[#000000]"
                              }`}
                            >
                              <span className="text-[15px]">N</span>
                              {calculateTotalPrice(plan)}
                              ₦{billingType === "Monthly"
                                ? plan.total
                                : plan.totalYearly}
                            </div>
                            <div
                              className={`text-sm ${
                                plan.isVip ? "text-[#90CAF9]" : "text-gray-600"
                              }`}
                            >
                              Total
                            </div>
                          </div> */}
                        </div>
                        <div
                          className={`mt-2 text-[12px] font-medium ${
                            plan.isVip ? "text-[#90CAF9]" : "text-[#616161]"
                          }`}
                        >
                          Setup fee: <span className="text-[9px]"></span>
                          <span
                            className={
                              plan.isVip ? "text-[#BBDEFB]" : "text-[#000000]"
                            }
                          >
                            {plan.integration}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  <ul className="space-y-4 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          <Check
                            className={`w-5 h-5 text-green-400 ${
                              plan.isVip ? "text-[#ffffff]" : "text-[#616161]"
                            }`}
                          />
                        </div>
                        <span
                          className={`text-sm leading-tight ${
                            plan.isVip ? "text-[#ffffff]" : "text-[#616161]"
                          }`}
                        >
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* Selected Add-On Features */}
                  {selectedFeatures && selectedFeatures.length > 0 && (
                    <div className="mb-2 pt-3 border-t-2 border-gray-200">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Selected Add-Ons:
                      </h4>
                      <div className="space-y-2">
                        {selectedFeatures.map((feature) => (
                          <div
                            key={feature.id}
                            className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 group hover:bg-blue-100 transition-colors"
                          >
                            <div className="flex-1">
                              <p className="text-sm font-medium text-blue-900">
                                {feature.displayName}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-blue-700">
                                {feature.price.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                              {onFeatureRemove && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onFeatureRemove(feature.id);
                                  }}
                                  className="p-1 hover:bg-blue-200 rounded-full transition-colors"
                                  title="Remove this add-on"
                                >
                                  <X className="w-4 h-4 text-blue-700" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Selected AI Add-ons */}
                  {selectedDirectAddOnIds &&
                    selectedDirectAddOnIds.length > 0 && (
                      <div className="mb-2 pt-3 border-t-2 border-gray-200">
                        <h3 className="text-sm font-medium text-gray-700 mb-2">
                          Selected AI Add-ons:
                        </h3>
                        <div className="space-y-2">
                          {selectedDirectAddOnIds.map((id) => {
                            const addon = (propAddons || []).find(
                              (a) => a.id === id,
                            );
                            if (!addon) return null;
                            return (
                              <div
                                key={addon.id}
                                className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 group hover:bg-blue-100 transition-colors"
                              >
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-blue-900">
                                    {addon.name}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-blue-900">
                                    {(typeof addon.price === "string"
                                      ? parseFloat(addon.price)
                                      : addon.price || 0
                                    ).toLocaleString(undefined, {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  </span>
                                  {onDirectAddOnRemove && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onDirectAddOnRemove(addon.id);
                                      }}
                                      className="p-1 hover:bg-blue-200 rounded-full transition-colors"
                                      title="Remove this add-on"
                                    >
                                      <X className="w-4 h-4 text-blue-900" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                  {/* Total Price Display */}
                  {((selectedFeatures && selectedFeatures.length > 0) ||
                    (selectedDirectAddOnIds &&
                      selectedDirectAddOnIds.length > 0)) && (
                    <div className="mb-5 px-3 py-2 text-sm bg-gradient-to-r from-blue-50 to-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">
                          Total Price:
                        </span>
                        <span className="text-sm font-medium text-blue-900 pr-8">
                          NGN {calculateTotalPrice(plan)}
                        </span>
                      </div>
                      <p className="text-[12px] text-gray-600 mt-1">
                        Base plan + setup fee +{" "}
                        {selectedFeatures.length +
                          selectedDirectAddOnIds.length}{" "}
                        add-on
                        {selectedFeatures.length +
                          selectedDirectAddOnIds.length !==
                        1
                          ? "s"
                          : ""}
                      </p>
                    </div>
                  )}

                  <button
                    onClick={() => setExpandedPlan(isExpanded ? null : index)}
                    className={`w-full py-2.5 px-3 mb-4 border rounded-lg font-medium hover:transition-all flex items-center justify-center gap-2 ${
                      plan.isVip
                        ? "border-[#1E88E5] text-white hover:bg-[#1976D2]"
                        : "border-[#EEEEEE] text-[#616161] hover:border-[#42A5F5] hover:bg-[#E3F2FD]"
                    }`}
                  >
                    {isExpanded ? (
                      <>
                        <ChevronUp className="w-4 h-4" />
                        <span className="text-sm">Hide Details</span>
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4" />
                        <span className="text-sm">View Details</span>
                      </>
                    )}
                  </button>

                  {isExpanded && (
                    <div
                      className={`mb-6 pb-6 border-t-2 pt-6 ${
                        plan.isVip ? "border-[#1976D2]" : "border-[#F5F5F5]"
                      }`}
                    >
                      <div className="flex justify-between items-center mb-3">
                        <h4
                          className={`text-sm font-medium ${
                            plan.isVip ? "text-white" : "text-[#212121]"
                          }`}
                        >
                          Integration Services
                        </h4>
                        <span
                          className={`text-sm font-medium ${
                            plan.isVip ? "text-white" : "text-[#212121]"
                          }`}
                        >
                          Cost (NGN)
                        </span>
                      </div>
                      <ul className="space-y-2">
                        {plan.integrationServices.map((service, i) => (
                          <li
                            key={i}
                            className="flex items-start justify-between text-sm"
                          >
                            <span
                              className={
                                plan.isVip ? "text-[#BBDEFB]" : "text-[#616161]"
                              }
                            >
                              {service.name}
                            </span>
                            <span
                              className={`whitespace-nowrap ml-2 ${
                                plan.isVip ? "text-[#90CAF9]" : "text-[#616161]"
                              }`}
                            >
                              <span className="text-[10px]">N</span>
                              {service.cost}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <button
                    onClick={() => {
                      if (provider === "bank-transfer" && onBankTransferClick) {
                        // onBankTransferClick();
                        // Navigate to order-form with all necessary parameters
                        const frequency: Frequency =
                          billingType === "Yearly"
                            ? "YEARLY"
                            : billingType === "Quarterly"
                              ? "QUARTERLY"
                              : "MONTHLY";
                        const featureIdsParam =
                          selectedFeatureIds.length > 0
                            ? `&featureIds=${selectedFeatureIds.join(",")}`
                            : "";
                        const directAddOnIdsParam =
                          selectedDirectAddOnIds.length > 0
                            ? `&directAddOnIds=${selectedDirectAddOnIds.join(",")}`
                            : "";
                        window.location.href = `/order-form?Id=${id}${
                          selectedTier
                            ? `&tier=${plan.tier || selectedTier}`
                            : `&tier=${plan.tier}`
                        }&provider=${
                          provider || "paypal"
                        }&frequency=${frequency}${featureIdsParam}${directAddOnIdsParam}`;
                      } else {
                        // handleSubscription(plan);
                        // Navigate to order-form with all necessary parameters
                        const frequency: Frequency =
                          billingType === "Yearly"
                            ? "YEARLY"
                            : billingType === "Quarterly"
                              ? "QUARTERLY"
                              : "MONTHLY";
                        const featureIdsParam =
                          selectedFeatureIds.length > 0
                            ? `&featureIds=${selectedFeatureIds.join(",")}`
                            : "";
                        const directAddOnIdsParam =
                          selectedDirectAddOnIds.length > 0
                            ? `&directAddOnIds=${selectedDirectAddOnIds.join(",")}`
                            : "";
                        window.location.href = `/order-form?Id=${id}${
                          selectedTier
                            ? `&tier=${plan.tier || selectedTier}`
                            : `&tier=${plan.tier}`
                        }&provider=${
                          provider || "paypal"
                        }&frequency=${frequency}${featureIdsParam}${directAddOnIdsParam}`;
                      }
                    }}
                    // disabled={isButtonDisabled(plan)}
                    className={`w-full py-3 px-6 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                      isCustom
                        ? plan.isVip
                          ? "bg-white text-[#0B60D6] hover:bg-[#F5F5F5] border border-white"
                          : "bg-[#F5F5F5] text-[#212121] hover:bg-[#EEEEEE] border border-[#e2e8f0]"
                        : plan.popular
                          ? "bg-gradient-to-r from-primary to-primary text-white hover:shadow-lg transform hover:scale-105"
                          : plan.isVip
                            ? "bg-white text-[#0B60D6] hover:bg-[#F5F5F5]"
                            : "bg-primary text-white hover:bg-[#1976D2]"
                    }`}
                  >
                    {provider === "bank-transfer" && onBankTransferClick
                      ? "Continue to Order Form"
                      : getButtonText(plan)}
                  </button>
                </div>
                {plan.isVip ? (
                  <div></div>
                ) : (
                  <div className="text-center flex justify-center items-center pb-4 px-6">
                    <div className="text-center flex justify-center items-center pb-4 px-6">
                      <button
                        onClick={() => setShowContactModal(true)}
                        className={`${
                          plan.isVip ? "text-white" : "text-[#0B60D6]"
                        } text-[14px] text-center hover:underline`}
                      >
                        Contact Sales
                      </button>
                      {/* <div className="flex justify-center pl-4">
                      <button onClick={() => setShowContactModal(true)}>
                        <Phone
                          size={20}
                          className={`${
                            plan.isVip ? "text-white" : "text-[#0B60D6]"
                          }`}
                        />
                      </button>
                    </div> */}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* View All Plans Button - Shows when single tier is displayed */}
        {/* {shouldShowViewAllButton && (
          <div className="flex justify-center mt-8">
            <button
              onClick={() => setShowAllPlans(true)}
              className="px-8 py-3 border-2 border-[#1976D2] text-[#1976D2] rounded-lg font-medium hover:bg-[#1976D2] hover:text-white transition-all duration-300 flex items-center gap-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
              </svg>
              Compare All Plans
            </button>
          </div>
        )} */}

        {/* Back to Selected Plan Button - Shows when all plans are displayed but a tier was pre-selected */}
        {shouldShowBackButton && (
          <div className="flex justify-center mt-8">
            <button
              onClick={() => setShowAllPlans(false)}
              className="px-8 py-3 border-2 border-[#1976D2] text-[#1976D2] rounded-lg font-medium hover:bg-[#1976D2] hover:text-white transition-all duration-300 flex items-center gap-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m15 18-6-6 6-6" />
              </svg>
              Back to Selected Plan
            </button>
          </div>
        )}
      </div>

      {/* Payment Success Modal */}
      {/* <PaymentSuccessModal
        isOpen={successModal.isOpen}
        onClose={() => setSuccessModal({ ...successModal, isOpen: false })}
        tier={successModal.tier}
        amount={successModal.amount}
        frequency={successModal.frequency}
      /> */}

      {/* Payment Failed Modal */}
      <PaymentFailedModal
        isOpen={failedModal.isOpen}
        onClose={() => setFailedModal({ ...failedModal, isOpen: false })}
        errorMessage={failedModal.errorMessage}
        onRetry={handleRetryPayment}
      />
    </section>
  );
}
